import { Camera, Object3D, Scene, Vector2, WebGLRenderer } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ViewPoint } from '../models/view-point';
import { lookFrom } from '../utility/viewer/camera-utility';
import { lookAtTarget } from '../utility/viewer/controls-utility';
import { clearScene } from '../utility/viewer/scene-utility';

export abstract class V3dSceneManager<TCamera extends Camera> {
  protected canvasWidth: number;
  protected canvasHeight: number;
  protected rootObject3d: Object3D;
  protected viewPoint: ViewPoint = ViewPoint.TopLeft;

  public controls: OrbitControls;

  constructor(public camera: TCamera, public scene: Scene, public renderer: WebGLRenderer) {
    this.createControls();
  }

  abstract updateCameraAspectRatio(): void;
  abstract lookAtObjectFromViewPoint(viewPoint: ViewPoint, target: Object3D, fitToView?: boolean): void;
  abstract fitTargetInView(viewPoint: ViewPoint, target: Object3D): void;

  public get canvasSize() {
    return new Vector2(this.canvasWidth, this.canvasHeight);
  }

  public setRootObject(object3d: Object3D, updateCamera = false): void {
    if (this.rootObject3d) {
      this.scene.remove(this.rootObject3d);
    }
    this.rootObject3d = object3d;
    this.scene.add(this.rootObject3d);
    this.rootObject3d.updateWorldMatrix(false, true);

    if (updateCamera) {
      this.lookAtObjectFromViewPoint(this.viewPoint, this.rootObject3d, true);
    }
  }

  public getRootObject() {
    return this.rootObject3d;
  }

  public setCanvasSize(width: number, height: number): void {
    this.canvasWidth = width;
    this.canvasHeight = height;
    this.renderer.setSize(width, height);
  }

  public setViewPoint(viewPoint: ViewPoint) {
    this.viewPoint = viewPoint;
  }

  public dispose() {
    clearScene(this.scene);
    this.renderer.dispose();
  }

  public render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  public lookAt(target: Object3D, lookFromViewPoint: ViewPoint, fitInView = true): void {
    const viewPoint = lookFromViewPoint ?? ViewPoint.TopLeft;

    lookFrom(this.camera, target, viewPoint);
    this.controls.update();
    lookAtTarget(this.controls, target);

    const shouldFitInView = fitInView ?? true;
    if (shouldFitInView) {
      this.fitTargetInView(viewPoint, target);
    }
  }

  private createControls(): void {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.minDistance = 1;
    this.controls.maxDistance = 10;
  }
}

export const createDefaultScene = () => new Scene();

export const createDefaultV3DRenderer = (canvasId?: string) => {
  const renderer = new WebGLRenderer({
    antialias: true,
    alpha: true,
  });

  if (canvasId) {
    renderer.domElement.id = canvasId;
  }
  return renderer;
};
